import React, { useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import axiosInstance from "../../../utils/axiosInstance";
import { servicesUrl, serviceTypesUrl } from "../../../utils/urls";
import { useNavigate, useParams } from "react-router-dom";

import PageHeader from "../../../components/page-layout/PageHeader";

import ServiceForm from "../../../components/pages/services/ServiceForm";
import ServicePrintPreference from "../../../components/pages/services/ServicePrintPreference";
import ServiceRequirementToggle from "../../../components/pages/services/ServiceTypeRequirementToggle";

import ServiceTypesForm from "../../../components/pages/services/ServiceTypesForm";
import LoadingUI from "../../../components/LoadingUI";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../../store/loaderSlice";
import { toast } from "react-hot-toast";

const EditService = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Print preference toggle state
  const [printPreference, setPrintPreference] = useState(true);

  // Service requirement toggle states - ADDED
  const [isRequired, setIsRequired] = useState(false);
  const [isDefault, setIsDefault] = useState(false);

  // Service type list state
  const [fetchedServiceTypes, setFetchedServiceTypes] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);

  // ids to delete service-types
  const [serviceTypeIdsToDelete, setServiceTypeIdsToDelete] = useState([]);

  useEffect(() => {
    setServiceTypes(fetchedServiceTypes);
  }, [fetchedServiceTypes]);

  // serviceTypes is valid if all name is not empty string
  const isServiceTypesValid = serviceTypes.some((s) => !s.name);

  const addNewServiceType = () => {
    setServiceTypes((prev) => [
      ...prev,
      {
        id: Date.now(), // Unique ID for the new service type
        name: "",
        file: null,
        preview: null,
      },
    ]);
  };

  // Validation schema with custom messages
  const validationSchema = Yup.object({
    serviceName: Yup.string().required("Service name is required"),
    description: Yup.string().required("Description is required"),
  });

  const { id } = useParams();
  const [initialValues, setInitialValues] = useState({
    serviceName: "",
    description: "",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([
      axiosInstance.get(`${servicesUrl}/${id}`),
      axiosInstance.get(`${serviceTypesUrl}?serviceId=${id}`),
    ])
      .then(([serviceRes, typesRes]) => {
        if (serviceRes.status === "fulfilled") {
          const data = serviceRes.value.data;
          setInitialValues({
            serviceName: data.name,
            description: data.description,
          });
          setPrintPreference(data.printPreference);
          // ADDED: Set requirement states
          setIsRequired(data.isRequired || false);
          setIsDefault(data.isDefault || false);
          console.log("Service data:", data);
        } else {
          console.error("Failed to load service:", serviceRes.reason);
        }

        if (typesRes.status === "fulfilled") {
          const data = typesRes.value.data;
          console.log("Service types response:", data);
          setFetchedServiceTypes(
            data.map((type) => ({
              name: type.name,
              file: null,
              preview: type.icon,
              id: type.id,
            }))
          );
        } else {
          console.error("Failed to load service types:", typesRes.reason);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          dispatch(showLoader("Updating Service..."));

          const serviceData = {
            name: values.serviceName,
            description: values.description,
            printPreference,
            isRequired, // ADDED
            isDefault,  // ADDED
          };
          const postItemsList = [],
            putItemsList = [];

          serviceTypes.forEach((st) => {
            // check is date or not
            // Check if st.id is a date (number generated by Date.now())
            if (
              typeof st.id === "number" &&
              new Date(st.id).toString() !== "Invalid Date" &&
              st.id > 1e12
            ) {
              postItemsList.push(st);
            } else {
              if (st.file) {
                putItemsList.push(st);
              } else {
                const isChanged = fetchedServiceTypes.some((fst) => {
                  const existObj = JSON.stringify(fst);
                  const obj = JSON.stringify(st);
                  console.log({ existObj, obj });
                  return existObj === obj;
                });
                if (!isChanged) {
                  putItemsList.push(st);
                }
              }
            }
          });
          console.log({ postItemsList, putItemsList });

          // 1) Patch the service - UPDATED to include isRequired and isDefault
          const { data: service } = await axiosInstance.patch(
            `${servicesUrl}/${id}`,
            {
              name: values.serviceName,
              description: values.description,
              printPreference,
              isRequired, // ADDED
              isDefault,  // ADDED
            }
          );

          // 2) Upload each icon as multipart/form-data
          const calls = await Promise.all(
            postItemsList.map(async (type) => {
              const formData = new FormData();
              formData.append("name", type.name);
              formData.append("serviceId", service.id);

              let fileToUpload = type.file;

              // If user didn't choose a file, fetch the default preview URL
              if (!fileToUpload && type.preview) {
                const res = await fetch(type.preview);
                const blob = await res.blob();
                const filename =
                  type.name.toLowerCase().replace(/\s+/g, "-") + ".png";
                fileToUpload = new File([blob], filename, {
                  type: blob.type,
                });
              }
              if (fileToUpload) formData.append("image", fileToUpload);

              return axiosInstance.post(serviceTypesUrl, formData);
            })
          );

          // 3) Upload each icon as multipart/form-data
          const putCalls = await Promise.all(
            putItemsList.map(async (type) => {
              const formData = new FormData();
              formData.append("name", type.name);
              formData.append("serviceId", service.id);

              let fileToUpload = type.file;

              // If user didn't choose a file, fetch the default preview URL
              if (!fileToUpload && type.preview) {
                const res = await fetch(type.preview);
                const blob = await res.blob();
                const filename =
                  type.name.toLowerCase().replace(/\s+/g, "-") + ".png";
                fileToUpload = new File([blob], filename, {
                  type: blob.type,
                });
              }
              if (fileToUpload) formData.append("image", fileToUpload);

              return axiosInstance.patch(
                `${serviceTypesUrl}/${type.id}`,
                formData
              );
            })
          );
          // 4) Delete service types
          const deleteCalls = await Promise.all(
            serviceTypeIdsToDelete.map(async (id) => {
              return axiosInstance.delete(`${serviceTypesUrl}/${id}`);
            })
          );
          toast.success("Service updated successfully");

          // Success: navigate to services list
          navigate("/services");
        } catch (error) {
          // Show error in console
          toast.error("Failed to update service");

          console.error(
            "Upload error:",
            error.response ? error.response.data : error
          );
        } finally {
          setSubmitting(false);
          dispatch(hideLoader());
        }
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <Form onSubmit={handleSubmit}>
          <Box>
            {/* Page header */}
            <PageHeader
              title={`Service: ${initialValues.serviceName}`}
              buttonOnClick="Save Changes"
              rightComponent={
                <Button
                  type="submit"
                  variant="outlined"
                  disabled={isSubmitting || isServiceTypesValid}
                >
                  {isSubmitting ? "Saving…" : "Save Changes"}
                </Button>
              }
              showBack
            />
            {loading ? (
              <LoadingUI />
            ) : (
              <>
                {/* Service form */}
                <ServiceForm />

                {/* Print preference and Service requirement toggles - SIDE BY SIDE */}
                <Box sx={{ display: "flex", gap: 4, alignItems: "flex-start", my: 5 }}>
                  <ServicePrintPreference
                    value={printPreference}
                    onChange={setPrintPreference}
                  />

                  <Box
                    sx={{
                      height: 60,
                      width: "1px",
                      bgcolor: "divider",
                      alignSelf: "stretch",
                      my: 1,
                    }}
                  />
                  <ServiceRequirementToggle
                    isRequired={isRequired}
                    isDefault={isDefault}
                    onRequiredChange={setIsRequired}
                    onDefaultChange={setIsDefault}
                  />
                </Box>

                {console.log({ serviceTypes })}
                <ServiceTypesForm
                  edit={true}
                  addNewServiceType={addNewServiceType}
                  serviceTypes={serviceTypes}
                  setServiceTypes={setServiceTypes}
                  fetchedServiceTypes={fetchedServiceTypes}
                  setServiceTypeIdsToDelete={setServiceTypeIdsToDelete}
                  // isDefault={isDefault}
                />
              </>
            )}
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default EditService;
